import { ReactNode } from 'react';

import useScrollbar, { ScrollAxis } from '../hooks/use-scrollbar';
import { styled } from '../theme';
import FlexBox from './FlexBox';

const SCROLLBAR_THICKNESS = 6;
const SCROLLBAR_GAP = 4;

const ScrollContainer = styled.div<{ axis: ScrollAxis }>(({ axis }) => ({
    // Hide the native overlay scrollbar; we render a persistent one alongside so
    // overflow is discoverable on macOS Chrome.
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    ...(axis === 'horizontal'
        ? { overflowX: 'auto', overflowY: 'hidden' }
        : { overflowX: 'hidden', overflowY: 'auto', flex: 1, minWidth: 0 }),
}));

const ScrollTrack = styled.div<{ axis: ScrollAxis }>(({ theme, axis }) => ({
    backgroundColor: theme.color.gray15,
    borderRadius: 3,
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    ...(axis === 'horizontal'
        ? { height: SCROLLBAR_THICKNESS, marginTop: SCROLLBAR_GAP }
        : { width: SCROLLBAR_THICKNESS, marginLeft: SCROLLBAR_GAP }),
}));

const ScrollThumb = styled.div<{ axis: ScrollAxis }>(({ theme, axis }) => ({
    backgroundColor: theme.color.gray40,
    borderRadius: 3,
    position: 'absolute',
    cursor: 'grab',
    userSelect: 'none',
    '&:active': {
        cursor: 'grabbing',
    },
    ...(axis === 'horizontal' ? { height: '100%', top: 0 } : { width: '100%', left: 0 }),
}));

type Props = {
    children: ReactNode;
    axis?: ScrollAxis;
    className?: string;
};

const ScrollArea = ({ children, axis = 'horizontal', className }: Props) => {
    const { scrollRef, thumb, onThumbPointerDown } = useScrollbar(axis);
    const isHorizontal = axis === 'horizontal';

    const thumbStyle = isHorizontal
        ? { width: thumb.size, left: thumb.offset }
        : { height: thumb.size, top: thumb.offset };

    return (
        <FlexBox direction={isHorizontal ? 'column' : 'row'} className={className}>
            <ScrollContainer ref={scrollRef} axis={axis}>
                {children}
            </ScrollContainer>
            {thumb.visible && (
                <ScrollTrack axis={axis}>
                    <ScrollThumb
                        axis={axis}
                        style={thumbStyle}
                        onPointerDown={onThumbPointerDown}
                    />
                </ScrollTrack>
            )}
        </FlexBox>
    );
};

export default ScrollArea;
